import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import AgoraRTC, {
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from "agora-rtc-sdk-ng";
import moment from "moment";
import VideoControls from "./VideoControls";
import EndInterviewModal from "./EndInterviewModal";
import TimeWarningModal from "./TimeWarningModal";
import WaitingScreen from "./WaitingScreen";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useInterviewApi } from "src/_common/hooks/actions/interview/appInterviewApiHook";
import { useScreeningApi } from "src/_common/hooks/actions/screening/appScreeningApiHook";
import { useAgoraRecordingApi } from "src/_common/hooks/actions/agora/appAgoraRecordingApiHook";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useAppUserDetailsSelector, useAppUserDataSelector } from "src/_common/hooks/selectors/userSelector";
import { CANDIDATE_STATUS, INTERVIEW_STATUS, ACTIONS } from "src/_config";
import "./interview-video.css";

interface InterviewVideoParams {
  interviewId: string;
}

interface VideoCallData {
  schedule_id: string;
  screening_id: string;
  job_title: string | null;
  interview_schedule: string;
  status: number;
  status_text: string;
  member_id: number;
  jobseeker_id: number;
  video_call_data: {
    appId: string;
    channelName: string;
    host: {
      uid: number;
      name: string;
      token: string;
      role: string;
    };
    guest: {
      uid: number;
      name: string;
      token: string;
      role: string;
    };
    expireTime: number;
    expiresIn: number;
    isExpired: boolean;
  };
  interview_url: string;
  interview_status: number;
}

const InterviewVideo: React.FC = () => {
  const { interviewId } = useParams<InterviewVideoParams>();
  const history = useHistory();
  const toast = useToaster();
  const dispatch = useDispatch();
  const interviewApi = useInterviewApi();
  const screeningApi = useScreeningApi();
  const agoraRecordingApi = useAgoraRecordingApi();
  const userApi = useUserApi();
  const userDetails = useAppUserDetailsSelector();
  const userData = useAppUserDataSelector();

  // Check if user is on free plan — default to true (free) until data loads to prevent flash
  const isFreePlan = userData == null || userData?.subscription?.price === 0;

  const [isJoined, setIsJoined] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [videoCallData, setVideoCallData] = useState<VideoCallData | null>(null);
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [localVideoTrack, setLocalVideoTrack] = useState<ICameraVideoTrack | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<IMicrophoneAudioTrack | null>(null);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  // Agora client is created lazily — only when initializeAgoraClient runs (not during wait)
  const clientRef = useRef<IAgoraRTCClient | null>(null);
  const getAgoraClient = (): IAgoraRTCClient => {
    if (!clientRef.current) {
      clientRef.current = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
    }
    return clientRef.current;
  };

  const [isWaiting, setIsWaiting] = useState(false);
  const [showEndInterviewModal, setShowEndInterviewModal] = useState(false);
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);
  const [isRecordingStarted, setIsRecordingStarted] = useState(false);
  const [recordingResourceId, setRecordingResourceId] = useState<string | null>(null);
  const [recordingSid, setRecordingSid] = useState<string | null>(null);
  const [remainingTime, setRemainingTime] = useState<number>(30 * 60); // Safe default, overwritten by calculateRemainingTime
  const [showTimeWarning, setShowTimeWarning] = useState<boolean>(false);
  const [timeWarningShown, setTimeWarningShown] = useState<boolean>(false);

  // User names from API
  const [localUserName, setLocalUserName] = useState<string>("You");
  const [remoteUserName, setRemoteUserName] = useState<string>("Participant");

  // Track if current user is host
  const [isCurrentUserHost, setIsCurrentUserHost] = useState<boolean>(false);
  // Track host UID to detect when host leaves
  const [hostUid, setHostUid] = useState<number | null>(null);

  // Refs to avoid stale closures in Agora event handlers
  const isCurrentUserHostRef = useRef(false);
  const hostUidRef = useRef<number | null>(null);
  const leaveCallRef = useRef<((showToast?: boolean) => Promise<void>) | null>(null);
  const remoteUsersRef = useRef<IAgoraRTCRemoteUser[]>([]);
  const autoCancelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    remoteUsersRef.current = remoteUsers;
  }, [remoteUsers]);

  // Always fetch fresh profile and subscription data when interview page opens.
  useEffect(() => {
    userApi.callGetMe(
      () => {}, // saga handles Redux dispatch
      () => {}, // silently fail
    );
    userApi.callGetUserData(
      (_message: string, response: any) => {
        dispatch({ type: ACTIONS.USER.SET_USER_DATA, payload: response });
      },
      () => {}, // silently fail — isFreePlan defaults to true (safe)
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!interviewId) {
      return;
    }
    fetchVideoCallData();

    return () => {
      // Cleanup when component unmounts
      cleanupCall();
    };
  }, [interviewId]);

  // Timer effect - counts down from 30 minutes to 0
  useEffect(() => {
    if (isLoading || !isJoined || joinError) return;

    const timerInterval = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 0) {
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [isLoading, isJoined, joinError]);

  // Check for 5-minute warning (when remaining time is 5 minutes = 300 seconds)
  useEffect(() => {
    // Show warning at 5 minutes remaining if not shown yet
    if (remainingTime === 300 && !timeWarningShown) {
      setShowTimeWarning(true);
      setTimeWarningShown(true);
    }
  }, [remainingTime, timeWarningShown]);

  // Automatically end call when time reaches 0
  useEffect(() => {
    if (remainingTime === 0 && isJoined) {
      setShowTimeWarning(false); // Hide warning modal when interview ends
      toast.warning("Interview time has ended");
      leaveCall(false);
    }
  }, [remainingTime, isJoined]);

  // Auto-cancel if candidate hasn't joined within 10 minutes of scheduled start time
  useEffect(() => {
    if (!isJoined || !isCurrentUserHost || !videoCallData?.schedule_id) return;

    // Candidate already in room — no timer needed
    if (remoteUsers.length > 0) return;

    const scheduleDate = moment.utc(videoCallData.interview_schedule).local().toDate();
    const deadline = scheduleDate.getTime() + 10 * 60 * 1000;
    const delay = Math.max(0, deadline - Date.now());

    autoCancelTimerRef.current = setTimeout(() => {
      if (remoteUsersRef.current.length === 0) {
        toast.warning("No candidate joined within 10 minutes. The interview has been automatically cancelled.");
        interviewApi.callRescheduleOrCancel(
          { schedule_id: videoCallData.schedule_id, interview_status: INTERVIEW_STATUS.CANCEL },
          () => {
            leaveCallRef.current?.(false);
          },
          () => {
            leaveCallRef.current?.(false);
          },
        );
      }
    }, delay);

    return () => {
      if (autoCancelTimerRef.current) clearTimeout(autoCancelTimerRef.current);
    };
  }, [isJoined, isCurrentUserHost]);

  // Play local video when track is available and component is mounted
  useEffect(() => {
    if (localVideoTrack && isJoined && isMediaLoaded) {
      const localPlayer = document.getElementById("local-player");
      if (localPlayer) {
        try {
          localVideoTrack.play("local-player");
          console.log("Local video playing");
        } catch (error) {
          console.error("Error playing local video:", error);
        }
      }
    }
  }, [localVideoTrack, isJoined, isMediaLoaded]);

  // Calculate remaining time based on interview_schedule from API
  const calculateRemainingTime = (videoData: VideoCallData) => {
    const scheduleStr = videoData?.interview_schedule;
    if (scheduleStr) {
      const scheduleDate = moment.utc(scheduleStr).local();
      const endTime = scheduleDate.clone().add(30, "minutes");
      const now = moment();
      const remainingSeconds = Math.max(0, endTime.diff(now, "seconds"));
      setRemainingTime(remainingSeconds);
    } else {
      // Fallback: if no schedule, use 30 minutes
      setRemainingTime(30 * 60);
    }
  };

  const fetchVideoCallData = () => {
    setIsLoading(true);

    interviewApi.callGetVideoCallData(
      { interviewId },
      (message: any, response: any) => {
        const videoData = response;
        setVideoCallData(videoData);

        // Redirect with toast based on interview_status
        const interviewStatus = videoData?.interview_status;
        if (interviewStatus === INTERVIEW_STATUS.CANCEL) {
          toast.error("This interview has been cancelled.");
          history.push("/dashboard");
          return;
        } else if (interviewStatus === INTERVIEW_STATUS.COMPLETE) {
          toast.error("This interview has already been completed.");
          history.push("/dashboard");
          return;
        } else if (interviewStatus === INTERVIEW_STATUS.RESCHEDULE) {
          toast.error("This interview has been rescheduled.");
          history.push("/dashboard");
          return;
        }

        // Check if the interview hasn't started yet
        const scheduleStr = videoData?.interview_schedule;
        if (scheduleStr) {
          const scheduleDate = moment.utc(scheduleStr).local().toDate();
          const msUntilStart = scheduleDate.getTime() - Date.now();
          if (msUntilStart > 0) {
            // Show waiting screen — do NOT initialise Agora yet
            setIsWaiting(true);
            setIsLoading(false);
            return;
          }
        }

        // Schedule is in the past or missing — join immediately
        calculateRemainingTime(videoData);
        setIsLoading(false);
        initializeAgoraClient(videoData);
      },
      (error: any) => {
        toast.error(error);
        setIsLoading(false);
        //
      },
    );
  };

  // Called by WaitingScreen when the scheduled time arrives
  const handleScheduleReached = () => {
    setIsWaiting(false);
    if (videoCallData) {
      calculateRemainingTime(videoCallData);
      initializeAgoraClient(videoCallData);
    }
  };

  // Returns the name of the other participant for the waiting screen
  const resolveCounterpartName = (data: VideoCallData): string => {
    if (userDetails && data.member_id === userDetails?.user?.id) {
      return data.video_call_data?.guest?.name || "Your Candidate";
    }
    return data.video_call_data?.host?.name || "Your Interviewer";
  };

  const startRecording = () => {
    if (!videoCallData || isRecordingStarted) {
      return;
    }

    const recordingData = {
      channel_name: videoCallData.video_call_data.channelName,
      uid: videoCallData.video_call_data.host.uid.toString(),
      schedule_id: videoCallData.schedule_id,
    };

    agoraRecordingApi.callStartRecording(
      recordingData,
      (message: string, response: any) => {
        setIsRecordingStarted(true);
        // Store resourceId and sid from the response
        if (response?.resourceId) {
          setRecordingResourceId(response.resourceId);
        }
        if (response?.sid) {
          setRecordingSid(response.sid);
        }
        toast.success(message || "Recording started");
        console.log("Recording started successfully:", response);
      },
      (error: string) => {
        console.error("Failed to start recording:", error);
        toast.error("Failed to start recording");
      },
    );
  };

  const stopRecording = (): Promise<void> => {
    return new Promise((resolve) => {
      if (!videoCallData || !isRecordingStarted || !recordingResourceId || !recordingSid) {
        console.warn("Cannot stop recording: missing required data", {
          isRecordingStarted,
          hasResourceId: !!recordingResourceId,
          hasSid: !!recordingSid,
        });
        resolve();
        return;
      }

      const recordingData = {
        channel_name: videoCallData.video_call_data.channelName,
        uid: videoCallData.video_call_data.host.uid.toString(),
        resource_id: recordingResourceId,
        sid: recordingSid,
        schedule_id: videoCallData.schedule_id,
      };

      agoraRecordingApi.callStopRecording(
        recordingData,
        (message: string, response: any) => {
          setIsRecordingStarted(false);
          setRecordingResourceId(null);
          setRecordingSid(null);
          toast.success(message || "Recording stopped");
          console.log("Recording stopped successfully:", response);
          resolve();
        },
        (error: string) => {
          console.error("Failed to stop recording:", error);
          toast.error("Failed to stop recording");
          resolve(); // resolve anyway so cleanup continues
        },
      );
    });
  };

  const handleToggleRecording = () => {
    if (isRecordingStarted) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleUserPublished = async (user: IAgoraRTCRemoteUser, mediaType: "audio" | "video") => {
    try {
      // Enforce 2-user limit: don't subscribe to new users when already at capacity
      const currentRemote = remoteUsersRef.current;
      const isExistingUser = currentRemote.some((u) => u.uid === user.uid);
      if (!isExistingUser && currentRemote.length >= 1) {
        return;
      }

      await getAgoraClient().subscribe(user, mediaType);

      // Add user to remoteUsers if not already present (for both audio and video)
      setRemoteUsers((prev) => {
        const exists = prev.find((u) => u.uid === user.uid);
        if (exists) {
          // Update the existing user reference
          return prev.map((u) => (u.uid === user.uid ? user : u));
        }
        if (prev.length >= 1) return prev; // Enforce 2-user limit
        return [...prev, user];
      });

      if (mediaType === "video") {
        // Play remote video
        // setTimeout(() => {
        const player = document.getElementById(`remote-player-${user.uid}`);

        if (player && user.videoTrack) {
          user.videoTrack.play(`remote-player-${user.uid}`);
        }
        // }, 100);
      }

      if (mediaType === "audio" && user.audioTrack) {
        // Add a small delay to ensure the audio track is fully ready
        // setTimeout(() => {
        if (user.audioTrack) {
          // Set volume to maximum to ensure audio is audible
          user.audioTrack.setVolume(100);
          // Play the audio track
          user.audioTrack.play();
        }
        // }, 100);
      }
    } catch (error) {}
  };

  const handleUserUnpublished = (user: IAgoraRTCRemoteUser, mediaType: "audio" | "video") => {
    // Keep the user in the list, just update their reference
    // The video/audio track will be undefined and we'll show appropriate indicators
    setRemoteUsers((prev) => {
      return prev.map((u) => (u.uid === user.uid ? user : u));
    });
  };

  const handleUserJoined = (user: IAgoraRTCRemoteUser, showNotification: boolean = true) => {
    // Add user to the list when they join
    setRemoteUsers((prev) => {
      // Enforce 2-user limit: only allow 1 remote user
      if (prev.length >= 1 && !prev.find((u) => u.uid === user.uid)) {
        return prev;
      }
      const exists = prev.find((u) => u.uid === user.uid);
      if (exists) {
        return prev;
      }
      return [...prev, user];
    });
    if (showNotification) {
      toast.show("A participant has joined the interview");
    }
  };

  const handleUserLeft = (user: IAgoraRTCRemoteUser) => {
    setRemoteUsers((prev) => {
      const filtered = prev.filter((u) => u.uid !== user.uid);

      return filtered;
    });

    // Check if the user who left is the host and current user is guest
    // Use refs to get latest values (avoids stale closure in Agora event handlers)
    if (!isCurrentUserHostRef.current && hostUidRef.current && user.uid === hostUidRef.current) {
      // Host has left, automatically end the interview for the guest
      toast.show("The host has ended the interview");
      setTimeout(() => {
        leaveCallRef.current?.(false);
      }, 2000); // Give user time to see the message
    } else {
      toast.show("A participant has left the interview");
    }
  };

  const initializeAgoraClient = async (callData?: VideoCallData) => {
    const client = getAgoraClient();
    try {
      if (!callData) {
        const errorMsg = "Invalid video call data - No data received";
        toast.error(errorMsg);
        setJoinError(errorMsg);
        setIsLoading(false);
        return;
      }

      if (!callData.video_call_data) {
        const errorMsg = "Invalid video call data ";
        toast.error(errorMsg);
        setJoinError(errorMsg);
        setIsLoading(false);
        return;
      }

      const { appId, channelName, host, guest } = callData.video_call_data;

      // Validate required fields
      if (!appId || !channelName) {
        const errorMsg = "Invalid video call configuration";
        toast.error(errorMsg);
        setJoinError(errorMsg);
        setIsLoading(false);
        return;
      }

      // Trim whitespace and validate appId format
      const trimmedAppId = appId.trim();
      if (trimmedAppId.length === 0) {
        const errorMsg = "Invalid Room ID";
        toast.error(errorMsg);
        setJoinError(errorMsg);
        setIsLoading(false);
        return;
      }

      if (!host && !guest) {
        const errorMsg = "Invalid video call configuration ";
        toast.error(errorMsg);
        setJoinError(errorMsg);
        setIsLoading(false);
        return;
      }

      // Determine if current user is host or guest based on member_id/jobseeker_id
      let credentials;
      let userRole = "unknown";
      let isHost = false;

      // Check if user is authenticated and has user details
      if (userDetails) {
        if (callData.member_id == userDetails?.user?.id) {
          // Current user is the host (employer/member)
          credentials = host;
          userRole = "host (member)";
          isHost = true;
        } else if (callData.jobseeker_id == userDetails?.user?.id) {
          // Current user is the guest (jobseeker)
          credentials = guest;
          userRole = "guest (jobseeker)";
          isHost = false;
        }
      }

      // Block users who are not the host or guest of this interview
      if (!credentials) {
        const errorMsg = "You are not authorized to join this interview";
        toast.error(errorMsg);
        setJoinError(errorMsg);
        setIsLoading(false);
        history.push("/dashboard");
        return;
      }

      if (!credentials || !credentials.token) {
        const errorMsg = "Invalid authentication credentials - No token available";
        toast.error(errorMsg);
        setJoinError(errorMsg);
        setIsLoading(false);
        return;
      }

      // Set user names based on role
      if (isHost) {
        setLocalUserName(host?.name || "You");
        setRemoteUserName(guest?.name || "Participant");
        setIsCurrentUserHost(true);
        isCurrentUserHostRef.current = true;
      } else {
        setLocalUserName(guest?.name || "You");
        setRemoteUserName(host?.name || "Participant");
        setIsCurrentUserHost(false);
        isCurrentUserHostRef.current = false;
        // Store host UID so we can detect when they leave
        setHostUid(host?.uid || null);
        hostUidRef.current = host?.uid || null;
      }

      // Set up event listeners BEFORE joining

      client.on("user-published", async (user, mediaType) => {
        if (mediaType === "audio" || mediaType === "video") {
          await handleUserPublished(user, mediaType);
        }
      });

      client.on("user-unpublished", (user, mediaType) => {
        if (mediaType === "audio" || mediaType === "video") {
          handleUserUnpublished(user, mediaType);
        }
      });

      client.on("user-left", (user) => {
        handleUserLeft(user);
      });

      client.on("user-joined", (user) => {
        handleUserJoined(user);
      });

      await client.join(trimmedAppId, channelName, credentials.token, credentials.uid);

      // Wait a moment for the channel state to update
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Check if there are already users in the channel
      const existingUsers = client.remoteUsers;

      // Enforce 2-user limit: if 2+ users already in channel, room is full
      if (existingUsers.length >= 2) {
        await client.leave();
        toast.error("This interview room is full. Only 2 participants are allowed.");
        setJoinError("This interview room is full. Only 2 participants are allowed.");
        setIsLoading(false);
        return;
      }

      // If current user is not the host (member), check if host is already in the channel
      // if (!isHost) {
      //   const hostIsPresent = existingUsers.some((user) => user.uid === host?.uid);

      //   if (!hostIsPresent) {
      //     // Host is not present, leave and show error
      //     await client.leave();
      //     history.push("/dashboard");
      //     toast.error("The interview host has not joined yet. Please wait for the host to join first.");
      //     setIsLoading(false);
      //     return;
      //   }
      // }

      // Add existing users to state first (don't subscribe yet)
      for (const user of existingUsers) {
        handleUserJoined(user, false);
      }

      // Create local tracks
      const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();

      setLocalAudioTrack(audioTrack);
      setLocalVideoTrack(videoTrack);

      // Publish local tracks
      await client.publish([audioTrack, videoTrack]);

      // Update state to show the UI (this renders the DOM elements)
      setIsJoined(true);
      setIsMediaLoaded(true);
      setIsLoading(false);

      // Play local video after a short delay to ensure DOM is ready
      setTimeout(() => {
        const localPlayer = document.getElementById("local-player");
        if (localPlayer) {
          videoTrack.play("local-player");
        } else {
          console.error("local-player element not found");
          // Retry after another delay
          setTimeout(() => {
            videoTrack.play("local-player");
          }, 500);
        }
      }, 100);

      // NOW subscribe to existing users after DOM is ready
      setTimeout(async () => {
        for (const user of existingUsers) {
          // Subscribe to their audio track if they have published audio
          if (user.hasAudio) {
            try {
              await client.subscribe(user, "audio");
              // After subscribing, the audioTrack should be available
              if (user.audioTrack) {
                // Set volume and play the audio track
                user.audioTrack.setVolume(100);
                user.audioTrack.play();
              }
            } catch (error) {
              console.error("Error subscribing to existing user audio:", error);
            }
          }

          // Subscribe to their video track if they have published video
          if (user.hasVideo) {
            try {
              await client.subscribe(user, "video");
              // After subscribing, play the video
              if (user.videoTrack) {
                setTimeout(() => {
                  const player = document.getElementById(`remote-player-${user.uid}`);
                  if (player) {
                    user.videoTrack!.play(`remote-player-${user.uid}`);
                    console.log(`Playing remote video for user ${user.uid}`);
                  } else {
                    console.error(`remote-player-${user.uid} element not found`);
                  }
                }, 100);
              }
            } catch (error) {
              console.error("Error subscribing to existing user video:", error);
            }
          }
        }
      }, 300);

      toast.show("You joined the interview");
    } catch (error: any) {
      let errorMessage = "Failed to join the interview";

      if (error?.code === "INVALID_VENDOR_KEY" || error?.message?.includes("invalid vendor key")) {
        errorMessage = "Invalid configuration. Please contact support.";
      } else if (error?.code === "INVALID_TOKEN" || error?.message?.includes("token")) {
        errorMessage = "Invalid or expired authentication token. Please refresh and try again.";
      } else if (error?.message) {
        errorMessage = `Failed to join the interview`;
      }

      setJoinError("Unable to join the interview at this time");
      setIsLoading(false);
      setIsMediaLoaded(true);
      // toast.error(errorMessage);
    }
  };

  const toggleVideo = async () => {
    if (localVideoTrack) {
      await localVideoTrack.setEnabled(isVideoMuted);
      setIsVideoMuted(!isVideoMuted);
    }
  };

  const toggleAudio = async () => {
    if (localAudioTrack) {
      await localAudioTrack.setEnabled(isAudioMuted);
      localAudioTrack.setVolume(100);
      setIsAudioMuted(!isAudioMuted);
      remoteUsers.forEach((user) => {
        if (user.audioTrack) {
          user.audioTrack.setVolume(100);
        }
      });
    }
  };

  const handleEndCallClick = () => {
    if (isCurrentUserHostRef.current) {
      setShowEndInterviewModal(true);
    } else {
      leaveCall(true);
    }
  };

  const cleanupCall = () => {
    // Synchronous cleanup for component unmount (doesn't navigate)
    try {
      // Stop recording if it was started
      if (isRecordingStarted && recordingResourceId && recordingSid && videoCallData) {
        stopRecording();
      }

      // Close local tracks
      if (localAudioTrack) {
        localAudioTrack.close();
      }
      if (localVideoTrack) {
        localVideoTrack.close();
      }

      // Remove all event listeners
      clientRef.current?.removeAllListeners();

      // Leave the channel
      if (isJoined) {
        clientRef.current?.leave().catch(() => {
          // Ignore errors during cleanup
        });
      }
    } catch (error) {
      // Ignore errors during cleanup
    }
  };

  const leaveCall = async (showToast: boolean = false) => {
    try {
      // Stop recording if it was started — await so API completes before cleanup
      if (isRecordingStarted) {
        await stopRecording();
      }

      // Close local tracks
      if (localAudioTrack) {
        localAudioTrack.close();
      }
      if (localVideoTrack) {
        localVideoTrack.close();
      }

      // Remove remote users
      setRemoteUsers([]);

      // Remove all event listeners
      clientRef.current?.removeAllListeners();

      // Leave the channel
      if (isJoined) {
        await clientRef.current?.leave();
      }

      setIsJoined(false);

      if (showToast) {
        toast.show("You have left the interview");
      }

      history.push("/dashboard");
    } catch (error) {}
  };

  // Keep leaveCallRef in sync so Agora event handlers always call the latest version
  leaveCallRef.current = leaveCall;

  const handleEndInterviewConfirm = () => {
    if (!videoCallData?.screening_id) {
      toast.error("Unable to end interview: Missing screening ID");
      setShowEndInterviewModal(false);
      return;
    }

    // If interview is already completed, just leave without calling update APIs
    if (videoCallData?.interview_status === INTERVIEW_STATUS.COMPLETE) {
      setShowEndInterviewModal(false);
      leaveCall(false);
      return;
    }

    // First, call API to update interview status to 3 (completed)
    if (videoCallData?.schedule_id) {
      interviewApi.callRescheduleOrCancel(
        {
          schedule_id: videoCallData.schedule_id,
          interview_status: INTERVIEW_STATUS.COMPLETE,
        },
        (message: string, resp: any) => {
          console.log("Interview status updated to completed:", resp);
          // Then update screening status
          screeningApi.callUpdateStatus(
            {
              screening_id: videoCallData.screening_id,
              status: CANDIDATE_STATUS.INTERVIEW_DONE,
            },
            (message: string, resp: any) => {
              setShowEndInterviewModal(false);
              leaveCall(false);
            },
            (error: string) => {
              toast.error("Failed to update interview status. Please try again.");
              setShowEndInterviewModal(false);
            },
          );
        },
        (error: string) => {
          console.error("Failed to update interview status:", error);
          toast.error("Failed to complete interview. Please try again.");
          setShowEndInterviewModal(false);
        },
      );
    } else {
      // If schedule_id is missing, just update screening status
      screeningApi.callUpdateStatus(
        {
          screening_id: videoCallData.screening_id,
          status: CANDIDATE_STATUS.INTERVIEW_DONE,
        },
        (message: string, resp: any) => {
          setShowEndInterviewModal(false);
          leaveCall(false);
        },
        (error: string) => {
          toast.error("Failed to update interview status. Please try again.");
          setShowEndInterviewModal(false);
        },
      );
    }
  };

  const handleEndInterviewCancel = () => {
    setShowEndInterviewModal(false);
  };

  // Format remaining time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleTimeWarningClose = () => {
    setShowTimeWarning(false);
  };

  return (
    <div className="drjob-iv-main-container">
      {/* Drjob Logo */}
      <div className="drjob-iv-logo-container" style={{ textAlign: "center", marginBottom: 24 }}>
        <img src="/images/dr-logo.png" alt="Drjob Logo" style={{ height: 48, objectFit: "contain" }} />
      </div>
      {isLoading ? (
        <div className="drjob-iv-header-section">
          <h1 className="drjob-iv-header-title">Loading Interview...</h1>
          <p className="drjob-iv-header-id">Please wait</p>
        </div>
      ) : isWaiting && videoCallData ? (
        <WaitingScreen
          videoCallData={videoCallData}
          counterpartName={resolveCounterpartName(videoCallData)}
          onScheduleReached={handleScheduleReached}
          showRecordingNotice={!isFreePlan && videoCallData?.member_id === userDetails?.user?.id}
        />
      ) : (
        // : joinError ? (
        //   <>
        //     <div className="drjob-iv-header-section">
        //       <h1 className="drjob-iv-header-title">Interview Session</h1>
        //     </div>
        //     <div className="drjob-iv-error-container">
        //       <div className="drjob-iv-error-content">
        //         <i
        //           className="fa fa-exclamation-circle"
        //           style={{ fontSize: "64px", color: "#e74c3c", marginBottom: "20px" }}
        //         ></i>
        //         <h2 style={{ color: "white", fontSize: "1.5rem", marginBottom: "16px" }}>Connection Failed</h2>
        //         <p style={{ color: "white", fontSize: "1.1rem", marginBottom: "24px", opacity: 0.9 }}>{joinError}</p>
        //         <button
        //           onClick={() => history.push("/dashboard")}
        //           style={{
        //             padding: "12px 32px",
        //             fontSize: "1rem",
        //             fontWeight: "600",
        //             color: "white",
        //             background: "#3498db",
        //             border: "none",
        //             borderRadius: "8px",
        //             cursor: "pointer",
        //             transition: "background 0.3s ease",
        //           }}
        //           onMouseOver={(e) => (e.currentTarget.style.background = "#2980b9")}
        //           onMouseOut={(e) => (e.currentTarget.style.background = "#3498db")}
        //         >
        //           Return to Dashboard
        //         </button>
        //       </div>
        //     </div>
        //   </>
        // )
        <>
          <div className="drjob-iv-header-section">
            <h1 className="drjob-iv-header-title">Interview Session</h1>
            {/* Remaining Time Display */}
            {isJoined && (
              <div className={`drjob-iv-remaining-time ${remainingTime <= 300 ? "time-warning" : "time-normal"}`}>
                <i className="fa fa-clock-o"></i>
                <span>Remaining Time: {formatTime(remainingTime)}</span>
              </div>
            )}
            {/* Recording Notice — shown to host on paid plans */}
            {isJoined && !isFreePlan && videoCallData?.member_id === userDetails?.user?.id && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  backgroundColor: "#fff8e1",
                  border: "1px solid #f59e0b",
                  borderRadius: "8px",
                  padding: "10px 16px",
                  marginTop: "12px",
                  fontSize: "13px",
                  color: "#92400e",
                  textAlign: "left",
                }}
              >
                <i
                  className="fa fa-exclamation-triangle"
                  style={{ marginTop: "2px", color: "#f59e0b", flexShrink: 0 }}
                ></i>
                <span>
                  <strong>Recording Notice:</strong> This interview is not automatically recorded by the system. Please
                  ensure you record the session manually from your end if a recording is needed.
                </span>
              </div>
            )}
            {/* <p className="drjob-iv-header-id">Room ID: {videoCallData?.video_call_data?.channelName || interviewId}</p> */}
          </div>

          <div className="drjob-iv-video-grid">
            {/* Local Video */}
            <div className="drjob-iv-video-wrapper drjob-iv-local-stream">
              <div id="local-player" className="drjob-iv-video-element"></div>
              {isMediaLoaded && !localVideoTrack && !isVideoMuted && (
                <div className="drjob-iv-video-unavailable">
                  <div className="drjob-iv-unavailable-content">
                    <i className="fa fa-exclamation-triangle" style={{ fontSize: "48px", marginBottom: "12px" }}></i>
                    <p>Camera not available</p>
                    <p style={{ fontSize: "0.85rem", marginTop: "8px", opacity: 0.8 }}>
                      Please check camera permissions
                    </p>
                  </div>
                </div>
              )}
              {isVideoMuted && localVideoTrack && (
                <div className="drjob-iv-video-unavailable">
                  <div className="drjob-iv-unavailable-content">
                    <i className="fa fa-video-slash" style={{ fontSize: "48px", marginBottom: "12px" }}></i>
                    <p>Video is turned off</p>
                  </div>
                </div>
              )}
              {isMediaLoaded && !localAudioTrack && !isAudioMuted && (
                <div className="drjob-iv-audio-unavailable-indicator">
                  <i className="fa fa-exclamation-circle" style={{ fontSize: "20px" }}></i>
                  <span style={{ marginLeft: "8px" }}>Microphone not available</span>
                </div>
              )}
              <div className="drjob-iv-stream-label">
                {localUserName} {isVideoMuted && <span className="drjob-iv-muted-indicator">(Video Off)</span>}
                {isAudioMuted && <span className="drjob-iv-muted-indicator">(Muted)</span>}
                {isMediaLoaded && !localVideoTrack && !isVideoMuted && (
                  <span className="drjob-iv-muted-indicator">(No Camera)</span>
                )}
                {isMediaLoaded && !localAudioTrack && !isAudioMuted && (
                  <span className="drjob-iv-muted-indicator">(No Mic)</span>
                )}
              </div>
            </div>

            {/* Remote Videos */}
            {remoteUsers.map((user) => (
              <div key={user.uid} className="drjob-iv-video-wrapper drjob-iv-remote-stream">
                <div id={`remote-player-${user.uid}`} className="drjob-iv-video-element"></div>
                {!user.videoTrack && (
                  <div className="drjob-iv-video-unavailable">
                    <div className="drjob-iv-unavailable-content">
                      <i className="fa fa-video-slash" style={{ fontSize: "48px", marginBottom: "12px" }}></i>
                      <p>Participant's video is unavailable</p>
                    </div>
                  </div>
                )}
                {!user.audioTrack && user.videoTrack && (
                  <div className="drjob-iv-audio-unavailable-indicator">
                    <i className="fa fa-microphone-slash" style={{ fontSize: "20px" }}></i>
                    <span style={{ marginLeft: "8px" }}>Audio unavailable</span>
                  </div>
                )}
                <div className="drjob-iv-stream-label">
                  {remoteUserName}
                  {!user.videoTrack && <span className="drjob-iv-muted-indicator"> (Video Off)</span>}
                  {!user.audioTrack && <span className="drjob-iv-muted-indicator"> (Muted)</span>}
                </div>
              </div>
            ))}

            {/* Placeholder when no remote users */}
            {remoteUsers.length === 0 && (
              <div className="drjob-iv-video-wrapper drjob-iv-waiting-state">
                <div className="drjob-iv-waiting-content">
                  <div className="drjob-iv-waiting-icon">
                    <i className="fa fa-users"></i>
                  </div>
                  <p>Waiting for other participants to join...</p>
                </div>
              </div>
            )}
          </div>

          {/* Video Controls */}
          {!isLoading && (
            <VideoControls
              isVideoMuted={isVideoMuted}
              isAudioMuted={isAudioMuted}
              onToggleVideo={toggleVideo}
              onToggleAudio={toggleAudio}
              onLeaveCall={handleEndCallClick}
              isRecording={isRecordingStarted}
              onToggleRecording={handleToggleRecording}
              showRecordingButton={
                !isFreePlan &&
                // userDetails?.user_type === "member" &&
                videoCallData?.member_id === userDetails?.user?.id
              }
            />
          )}
        </>
      )}

      {/* End Interview Confirmation Modal */}
      {showEndInterviewModal && (
        <EndInterviewModal
          isVisible={showEndInterviewModal}
          onConfirm={handleEndInterviewConfirm}
          onCancel={handleEndInterviewCancel}
        />
      )}

      {/* Time Warning Modal */}
      {showTimeWarning && (
        <TimeWarningModal
          isVisible={showTimeWarning}
          onClose={handleTimeWarningClose}
          remainingSeconds={remainingTime}
        />
      )}
    </div>
  );
};

export default InterviewVideo;
